/**
 * useAddOns Hook
 *
 * FERP Sprint 5 - Professional Features
 *
 * Fetches add-on catalog from backend for professional marketplace
 *
 * Architecture Compliance:
 * - ✅ Repository pattern via GraphQL query
 * - ✅ Domain-agnostic (all data from backend)
 * - ✅ Life-critical safety (error handling)
 * - ✅ Multi-tenant security (tenant from context)
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCurrentTenant } from './useCurrentTenant';

/**
 * GraphQL query for add-ons
 *
 * Maps to backend AddOnsQuery::add_ons
 * Source: backend/src/graphql/professional_features_schema.rs
 */
const ADD_ONS_QUERY = gql`
  query AddOns($input: AddOnsInput!) {
    addOns(input: $input) {
      addOns {
        id
        tenant
        name
        description
        category
        basePrice
        currency
        downloadCount
        rating
        createdAt
      }
      totalCount
    }
  }
`;

/**
 * Add-on entity (frontend representation)
 *
 * Maps to backend AddOnGql
 */
export interface AddOn {
  id: string;
  tenant: string;
  name: string;
  description: string;
  category: string;
  basePrice: number;
  currency: string;
  downloadCount: number;
  rating: number;
  createdAt: number; // Unix timestamp (milliseconds)
}

/**
 * Hook options for filtering add-ons
 */
export interface UseAddOnsOptions {
  category?: string; // Filter by category (e.g., "accounting", "healthcare")
  popularOnly?: boolean; // Show only popular add-ons (downloads >= 50)
}

/**
 * Hook return type
 */
export interface UseAddOnsResult {
  addOns: AddOn[];
  totalCount: number;
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<any>;
}

/**
 * useAddOns Hook
 *
 * Fetches add-ons from backend with filtering
 *
 * @param options - Filter options (category, popularOnly)
 * @returns Add-ons data, loading state, error state, refetch function
 *
 * @example
 * ```tsx
 * function AddOnMarketplace() {
 *   const { addOns, loading, error } = useAddOns({
 *     category: 'accounting',
 *     popularOnly: false
 *   });
 *
 *   if (loading) return <Loader />;
 *   if (error) return <Alert>Error: {error.message}</Alert>;
 *
 *   return (
 *     <Grid>
 *       {addOns.map(addOn => (
 *         <AddOnCard key={addOn.id} addOn={addOn} />
 *       ))}
 *     </Grid>
 *   );
 * }
 * ```
 */
export function useAddOns(
  options: UseAddOnsOptions = {}
): UseAddOnsResult {
  // ✅ FERP-Compliant: Get tenant from context (not hardcoded)
  const { tenantId } = useCurrentTenant();

  // Execute GraphQL query
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(ADD_ONS_QUERY, {
    variables: {
      input: {
        tenant: tenantId,
        category: options.category || null,
        popularOnly: options.popularOnly || false,
      },
    },
    fetchPolicy: 'cache-and-network', // Always check server for updates
    skip: !tenantId, // Don't query until tenant is available
  });

  // Extract add-ons from response
  const addOns = data?.addOns?.addOns || [];
  const totalCount = data?.addOns?.totalCount || 0;

  return {
    addOns,
    totalCount,
    loading,
    error,
    refetch,
  };
}
